import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import type { LucideIcon } from 'lucide-react';
import {
  AlertCircle,
  BookOpen,
  FileQuestion,
  Layers,
  Loader2,
  Pencil,
  Plus,
  Share2,
  Trash2,
} from 'lucide-react';
import { useTopics } from '../hooks/useTopics';
import { api } from '../services/api';

interface Stats {
  topics: number;
  words: number;
  quizzes: number;
  published: number;
}

function errorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError<{ error?: string }>(err) && err.response?.data?.error) {
    return err.response.data.error;
  }
  return fallback;
}

export default function TopicsPage() {
  const { topics, loading, createTopic, deleteTopic, updateTopic } = useTopics();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<{ id: number; title: string; description: string } | null>(null);
  const [form, setForm] = useState({ title: '', description: '' });
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{ id: number; title: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [stats, setStats] = useState<Stats | null>(null);

  // `GET /api/topics` carries no per-row counts, and asking `/api/topics/:id` for
  // every row would be an N+1. The aggregate from `/api/topics/stats` is enough for
  // this page and costs one request; per-topic numbers live on the topic page.
  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/topics/stats');
      setStats(res.data.data as Stats);
    } catch {
      // The list itself still works without the summary cards.
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const openCreate = () => {
    setEditing(null);
    setForm({ title: '', description: '' });
    setError('');
    setShowModal(true);
  };

  const openEdit = (topic: { id: number; title: string; description: string | null }) => {
    setEditing({ id: topic.id, title: topic.title, description: topic.description || '' });
    setForm({ title: topic.title, description: topic.description || '' });
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');
    try {
      if (editing) {
        await updateTopic(editing.id, form);
      } else {
        await createTopic(form);
      }
      setShowModal(false);
      fetchStats();
    } catch (err: unknown) {
      setError(errorMessage(err, 'Не удалось сохранить тему'));
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setIsDeleting(true);
    setError('');
    try {
      await deleteTopic(pendingDelete.id);
      setPendingDelete(null);
      fetchStats();
    } catch (err: unknown) {
      setError(errorMessage(err, 'Не удалось удалить тему'));
    } finally {
      setIsDeleting(false);
    }
  };

  const statCards: { label: string; value: number; icon: LucideIcon }[] = [
    { label: 'Тем', value: stats?.topics ?? 0, icon: Layers },
    { label: 'Слов', value: stats?.words ?? 0, icon: BookOpen },
    { label: 'Тестов', value: stats?.quizzes ?? 0, icon: FileQuestion },
    { label: 'Опубликовано', value: stats?.published ?? 0, icon: Share2 },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <Layers size={20} className="text-brand-600" />
          <h1 className="text-2xl font-bold text-gray-900">Темы</h1>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 self-start rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
        >
          <Plus size={16} />
          Новая тема
        </button>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {stats && (
        <div className="-mx-4 grid grid-cols-2 gap-4 overflow-x-auto px-4 sm:mx-0 sm:grid-cols-2 sm:overflow-visible sm:px-0 lg:grid-cols-4">
          {statCards.map((stat) => (
            <div
              key={stat.label}
              className="flex items-center rounded-xl border border-gray-100 bg-white p-4 shadow-sm"
            >
              <div className="mr-3 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
                <stat.icon size={20} />
              </div>
              <div className="min-w-0">
                <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                <div className="truncate text-xs text-gray-500">{stat.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
        </div>
      ) : topics.length === 0 ? (
        <div className="flex h-64 flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white">
          <Layers size={32} className="text-gray-300" />
          <p className="mt-2 text-sm text-gray-500">Пока нет тем. Создайте первую!</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {topics.map((topic) => (
            <div
              key={topic.id}
              className="group relative flex flex-col rounded-xl border border-gray-100 bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
            >
              <Link to={`/topics/${topic.id}`} className="block">
                <h3 className="break-words text-lg font-semibold text-gray-900">{topic.title}</h3>
                {topic.description && (
                  <p className="mt-1 line-clamp-2 break-words text-sm text-gray-500">{topic.description}</p>
                )}
              </Link>
              <div className="mt-4 flex items-center justify-between">
                <Link
                  to={`/topics/${topic.id}`}
                  className="text-sm font-medium text-brand-600 hover:text-brand-700"
                >
                  Открыть →
                </Link>
                {/* Hover-only actions are unreachable on touch devices, so they stay
                    visible below the sm breakpoint. */}
                <div className="flex items-center gap-1 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100">
                  <button
                    onClick={() => openEdit(topic)}
                    title="Редактировать тему"
                    aria-label="Редактировать тему"
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-brand-600"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => setPendingDelete({ id: topic.id, title: topic.title })}
                    title="Удалить тему"
                    aria-label="Удалить тему"
                    className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">
              {editing ? 'Редактировать тему' : 'Новая тема'}
            </h2>
            {error && (
              <div className="mb-4 flex items-start gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Название</label>
                <input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  required
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Описание</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  {isSaving && <Loader2 size={16} className="animate-spin" />}
                  {editing ? 'Сохранить' : 'Создать'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {pendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Удалить тему?</h3>
            <p className="mt-2 text-sm text-gray-500">
              «{pendingDelete.title}» будет удалена вместе со всеми словами и тестами. Действие
              необратимо.
            </p>
            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => setPendingDelete(null)}
                disabled={isDeleting}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Отмена
              </button>
              <button
                onClick={confirmDelete}
                disabled={isDeleting}
                className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {isDeleting && <Loader2 size={16} className="animate-spin" />}
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}